import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL as API_URL } from '../utils/api';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const LANGS = ['en', 'ru', 'tr'];

const EMPTY_FORM = {
  title: { en: '', ru: '', tr: '' },
  body: { en: '', ru: '', tr: '' },
};

const CreatePostModal = ({ isOpen, onClose, onSubmit, initialData = null, submitting = false }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState(i18n.language || 'en');
  const [files, setFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({
          title: {
            en: initialData.title?.en || '',
            ru: initialData.title?.ru || '',
            tr: initialData.title?.tr || '',
          },
          body: {
            en: initialData.body?.en || '',
            ru: initialData.body?.ru || '',
            tr: initialData.body?.tr || '',
          },
        });
        setExistingImages(initialData.images || []);
        setFiles([]);
      } else {
        setForm(EMPTY_FORM);
        setFiles([]);
        setExistingImages([]);
      }
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setFilePreviews(urls.map((url, i) => ({ name: files[i].name, url })));
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  if (!isOpen) return null;

  const totalImages = existingImages.length + files.length;

  const updateLangField = (field, lang, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = [];
    for (const file of selectedFiles) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(t('installationInstructions.errors.invalidFileType', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('installationInstructions.errors.invalidFileSize', { name: file.name }));
        continue;
      }
      validFiles.push(file);
    }

    const availableSlots = MAX_FILES - existingImages.length - files.length;
    if (validFiles.length > availableSlots) {
      toast.error(t('installationInstructions.errors.maxImages'));
      if (availableSlots > 0) {
        setFiles((prev) => [...prev, ...validFiles.slice(0, availableSlots)]);
      }
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!form.title.en.trim()) {
      toast.error(t('installationInstructions.errors.titleRequired'));
      return false;
    }
    if (!form.body.en.trim()) {
      toast.error(t('installationInstructions.errors.bodyRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formData = new FormData();
    formData.append('title.en', form.title.en.trim());
    if (form.title.ru.trim()) formData.append('title.ru', form.title.ru.trim());
    if (form.title.tr.trim()) formData.append('title.tr', form.title.tr.trim());
    formData.append('body.en', form.body.en.trim());
    if (form.body.ru.trim()) formData.append('body.ru', form.body.ru.trim());
    if (form.body.tr.trim()) formData.append('body.tr', form.body.tr.trim());

    files.forEach((file) => formData.append('images', file));

    if (existingImages.length > 0) {
      formData.append('existingImages', JSON.stringify(existingImages));
    }

    onSubmit(formData);
  };

  const tabLabels = {
    en: t('installationInstructions.form.tab.en'),
    ru: t('installationInstructions.form.tab.ru'),
    tr: t('installationInstructions.form.tab.tr'),
  };

  const titlePlaceholder = t('installationInstructions.form.titlePlaceholder', {
    lang: activeTab.toUpperCase(),
  });
  const bodyPlaceholder = t('installationInstructions.form.bodyPlaceholder', {
    lang: activeTab.toUpperCase(),
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {initialData
              ? t('installationInstructions.editButton')
              : t('installationInstructions.createButton')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="flex border-b border-gray-200">
              {LANGS.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setActiveTab(lang)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === lang
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tabLabels[lang]}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                {t('installationInstructions.form.titleLabel', {
                  lang: activeTab.toUpperCase(),
                })}
                {activeTab === 'en' && ' *'}
              </label>
              <input
                type="text"
                value={form.title[activeTab]}
                onChange={(e) => updateLangField('title', activeTab, e.target.value)}
                maxLength={200}
                className="form-input"
                placeholder={titlePlaceholder}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                {t('installationInstructions.form.bodyLabel', {
                  lang: activeTab.toUpperCase(),
                })}
                {activeTab === 'en' && ' *'}
              </label>
              <textarea
                value={form.body[activeTab]}
                onChange={(e) => updateLangField('body', activeTab, e.target.value)}
                maxLength={12000}
                rows={5}
                className="form-input h-auto py-2 resize-y"
                placeholder={bodyPlaceholder}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {form.body[activeTab].length}/12000
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                {t('installationInstructions.form.images')}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={totalImages >= MAX_FILES}
                className="btn-secondary flex items-center gap-2 w-full justify-center"
              >
                <Upload className="w-4 h-4" />
                {t('installationInstructions.form.imagesCount', { count: totalImages })}
              </button>

              {(existingImages.length > 0 || filePreviews.length > 0) && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {existingImages.map((url, idx) => (
                    <div key={`existing-${idx}`} className="relative group">
                      <img
                        src={`${API_URL}${url}`}
                        alt={`Existing ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-lg border"
                        onError={(e) => {
                          if (e.currentTarget.dataset.fallback !== '1') {
                            e.currentTarget.dataset.fallback = '1';
                            e.currentTarget.src = '/no_image.webp';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(idx)}
                        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title={t('installationInstructions.deleteButton')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {filePreviews.map((preview, idx) => (
                    <div key={`new-${idx}`} className="relative group">
                      <img
                        src={preview.url}
                        alt={preview.name}
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title={t('installationInstructions.deleteButton')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary">
              <X className="w-4 h-4" />
              {t('installationInstructions.form.cancel')}
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {initialData
                ? t('installationInstructions.form.update')
                : t('installationInstructions.form.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostModal;
